/* eslint-disable camelcase */
import { useState, useCallback, useEffect } from 'react'
import {
  SimpleForm,
  Confirm,
  useGetIdentity,
  // Toolbar,
  // SaveButton,
  useUpdate,
  useNotify,
  useGetList,
  TextField,
  SimpleShowLayout,
} from 'react-admin'
import {
  useFormContext,
  useFormState,
  // useWatch,
} from 'react-hook-form'
import { Grid } from '@mui/material'

import { ADMINS_FIELDS } from '../../../providers/localization/constant'
// import PersonIcon from '@mui/icons-material/Person'
// import { AvatarInput } from '../../inputs'
import { useUserContextProvider } from '../../../contexts/user-context'

import PermissionTable from '../roles-permission/PermissionTable'

import { extractPermisionSubmenu, generateValuePermissions } from '../../../utils/user'
import isEmpty from 'lodash/isEmpty'
import { RolePermission } from '../../../resources/roles-permissions/type'

export const FormContent = ({ updateData, openConfirm, handleDialogClose }): JSX.Element => {
  const { identity } = useGetIdentity()
  const { getValues, setValue } = useFormContext()
  const [rolesPermission, setRolesPermission] = useState<RolePermission[] | []>([])
  const [extractedPermissions, setExtractedPermissions] = useState<RolePermission[] | []>([])
  const [isRegistering, setisRegistering] = useState(true)
  const { data: permissionKeys, isLoading: isFetching } = useGetList('permissions', {
    sort: { field: 'key', order: 'ASC' },
  })

  /**
   * Effect #1
   * - set extract permisison state from record.permission
   * */
  useEffect(() => {
    if (identity?.role?.id && !isEmpty(identity?.role?.permissions)) {
      setExtractedPermissions(() => extractPermisionSubmenu(identity?.role?.permissions))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(identity?.role)])

  /**
   * Effect #2
   * - set the permissions value to react hook form
   * */
  useEffect(() => {
    if (!isEmpty(permissionKeys) && !isEmpty(extractedPermissions) && !isFetching) {
      // Iterate over each permission to register each permission of each feature to FormContext
      const registerPermissions = generateValuePermissions(permissionKeys, extractedPermissions)

      setValue('permissions', registerPermissions)
      setisRegistering(false)
    }
  }, [permissionKeys, isFetching, extractedPermissions, setValue])

  /**
   * Effect #3
   * - set rolesPermission state value from getValues (form)
   * */
  useEffect(() => {
    if (!isRegistering) setRolesPermission(() => getValues()?.permissions || [])
  }, [getValues, isRegistering])

  const notify = useNotify()
  // const { getValues } = useFormContext()
  const { dirtyFields } = useFormState()
  const { refreshUserinfo } = useUserContextProvider()
  const onError = useCallback(
    (error): void => {
      const errorMessage = error.response.data.message
      return notify(errorMessage || 'ra.validation.update_profile_fail', { type: 'warning' })
    },
    [notify],
  )

  const onSuccess = useCallback(async (): Promise<void> => {
    await refreshUserinfo()
    notify('ra.notification.update_profile_success')
  }, [notify, refreshUserinfo])

  const [update, { isLoading }] = useUpdate(undefined, undefined, { onError, onSuccess })

  const handleConfirm = useCallback(async (): Promise<void> => {
    const payload = {}
    for (const key in dirtyFields) {
      payload[key] = updateData[key]
    }
    await update('auth', {
      data: { encType: 'multipart/form-data', metaPath: 'auth/me', ...payload },
    })
    handleDialogClose()
  }, [dirtyFields, handleDialogClose, update, updateData])

  // const record = { id: null, ...getValues() }

  return (
    <>
      <Confirm
        isOpen={openConfirm}
        loading={isLoading}
        title='Update Profile'
        content='Are you sure you want to update your profile?'
        onConfirm={handleConfirm}
        onClose={handleDialogClose}
      />
      <Grid container spacing={1} justifyContent='center'>
        <Grid container item spacing={3} xs={12} sm={8} md={6}>
          <Grid item xs={12}>
            {/* <AvatarInput
              source='avatar'
              label='Avatar'
              placeholder={<PersonIcon />}
              accept='image/*'
              record={record}
            /> */}
          </Grid>
          <Grid item xs={12} sm={6}>
            <SimpleShowLayout>
              <TextField source='user_id' label={`${ADMINS_FIELDS}.user_id`} />
            </SimpleShowLayout>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SimpleShowLayout>
              <TextField source='email' label={`${ADMINS_FIELDS}.email`} />
            </SimpleShowLayout>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SimpleShowLayout>
              <TextField source='firstname_th' label={`${ADMINS_FIELDS}.firstname_th`} />
            </SimpleShowLayout>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SimpleShowLayout>
              <TextField source='lastname_th' label={`${ADMINS_FIELDS}.lastname_th`} />
            </SimpleShowLayout>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <PermissionTable
            rolePermissionList={rolesPermission}
            permissionKeys={permissionKeys}
            disabled
          />
        </Grid>
      </Grid>
    </>
  )
}

export const EditUser = (): JSX.Element => {
  const [updateData, setUpdateData] = useState([])
  const [openConfirm, setOpenConfirm] = useState(false)
  const handleDialogClose = (): void => setOpenConfirm(false)
  const { identity, isLoading: isIdentityLoading } = useGetIdentity()
  if (isIdentityLoading) return null

  const handleSubmit = (data): void => {
    setUpdateData(data)
    setOpenConfirm(true)
  }

  if (isIdentityLoading) return null
  return (
    <SimpleForm record={identity} onSubmit={handleSubmit} toolbar={null}>
      <FormContent
        updateData={updateData}
        openConfirm={openConfirm}
        handleDialogClose={handleDialogClose}
      />
    </SimpleForm>
  )
}

export default EditUser
